// 멀티테넌트 진입 시 활성 회사(compCd)를 해석한다.
// 우선순위: URL 경로 프리픽스(/{encOrg}/...) > ?org 쿼리 > sessionStorage > localStorage.
// 값을 sessionStorage에 보존하여 OIDC 리다이렉트 왕복 후에도 조직 스코프를 유지한다.

import { decodeOrgCode } from '@/lib/org-code';

const ENTRY_ORG_KEY = 'petrohq.entryOrg';
const LAST_ORG_KEY = 'petrohq.lastOrg';

/**
 * 활성 조직(compCd)을 우선순위로 해석한다.
 * 1. URL 경로 첫 세그먼트를 decodeOrgCode로 복원 (새 source-of-truth).
 *    react-router useParams가 아니라 window.location.pathname을 직접 파싱한다
 *    (keycloak init 타이밍에 라우터 컨텍스트 밖에서 호출되기 때문).
 * 2. URL의 ?org 값 (초대/레거시 진입) → 대문자.
 * 3. sessionStorage 폴백 (OIDC 왕복으로 쿼리/경로가 사라진 경우).
 * 4. localStorage 폴백 (재방문자).
 * 5. 모두 없으면 null.
 * 해석 성공 시 sessionStorage에 기록하고, 1·2에서 얻었으면 localStorage에도 기록한다.
 */
export function resolveEntryOrg(): string | null {
  if (typeof window === 'undefined') return null;

  const firstSegment = window.location.pathname.split('/')[1];
  const fromPath = firstSegment ? decodeOrgCode(firstSegment) : null;
  if (fromPath) {
    window.sessionStorage.setItem(ENTRY_ORG_KEY, fromPath);
    window.localStorage.setItem(LAST_ORG_KEY, fromPath);
    return fromPath;
  }

  const fromUrl = new URLSearchParams(window.location.search).get('org')?.trim();
  if (fromUrl) {
    const org = fromUrl.toUpperCase();
    window.sessionStorage.setItem(ENTRY_ORG_KEY, org);
    window.localStorage.setItem(LAST_ORG_KEY, org);
    return org;
  }

  // 저장값은 대문자 컨벤션 이전 세션의 소문자 잔존값일 수 있어 방어적으로 정규화한다.
  const fromSession = window.sessionStorage.getItem(ENTRY_ORG_KEY);
  if (fromSession) return fromSession.toUpperCase();

  const fromLocal = window.localStorage.getItem(LAST_ORG_KEY);
  return fromLocal ? fromLocal.toUpperCase() : null;
}

/**
 * Keycloak 로그인에 전달할 scope 문자열을 만든다.
 * org가 있으면 organization:{org}을 붙여 로그인을 해당 조직에 고정한다.
 */
export function buildAuthScope(org: string | null): string {
  return org ? `openid organization:${org}` : 'openid';
}
